import { createApi } from "@reduxjs/toolkit/query/react";

import type {
  ICreateRoleRequest,
  TCreateRoleResponse,
  TGetAllUserRoleDataRequest,
  TGetAllUserRoleDataResponse,
  TGetRoleByIDRequest,
  TGetRoleByIDResponse,
  TUpdateRoleRequest,
  TUpdateRoleResponse,
  TUserRoleDataRequest,
  TUserRoleDataResponse,
} from "@/types/(company)/roles.interface";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";

export const companyRolesApi = createApi({
  reducerPath: "companyRolesApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["getAllUserRoles", "getRoleById"],
  endpoints: (builder) => ({
    getAllUserRoles: builder.query<
      TGetAllUserRoleDataResponse,
      TGetAllUserRoleDataRequest
    >({
      query: () => {
        return {
          url: "/company/roles",
          method: "GET",
        };
      },
      providesTags: ["getAllUserRoles"],
    }),
    createRole: builder.mutation<TCreateRoleResponse, ICreateRoleRequest>({
      query: (data) => {
        return {
          url: "/company/roles",
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["getAllUserRoles"],
    }),
    updateRole: builder.mutation<TUpdateRoleResponse, TUpdateRoleRequest>({
      query: (data) => {
        return {
          url: "/company/roles/" + data.id,
          method: "PUT",
          body: data.data,
        };
      },
      invalidatesTags: ["getAllUserRoles", "getRoleById"],
    }),
    getRoleById: builder.query<TGetRoleByIDResponse, TGetRoleByIDRequest>({
      query: ({ id }) => {
        return {
          url: "/company/roles/" + id,
          method: "GET",
        };
      },
      providesTags: ["getRoleById"],
    }),
    deleteRole: builder.mutation<
      { message: string; success: boolean },
      { id: number }
    >({
      query: ({ id }) => ({
        url: "/company/roles/" + id,
        method: "DELETE",
      }),
      invalidatesTags: ["getAllUserRoles", "getRoleById"],
    }),
    assignUserRole: builder.mutation<
      TUserRoleDataResponse,
      TUserRoleDataRequest
    >({
      query: (body) => {
        return {
          url: "/user-roles/assign",
          method: "PUT",
          body,
        };
      },
    }),
  }),
});

export const {
  useGetRoleByIdQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useGetAllUserRolesQuery,
  useDeleteRoleMutation,
  useAssignUserRoleMutation,
} = companyRolesApi;
